import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';

interface CriticalNode {
    id: number;
    code: string;
    type: string;
    severity: string;
    status: string;
    description: string;
    detectedAt: string;
}

interface Stats {
    total: number;
    detected: number;
    underReview: number;
    resolved: number;
}

export default function CriticalNodesPage() {
    const { t } = useTranslation();
    const [nodes, setNodes] = useState<CriticalNode[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [nodesRes, statsRes] = await Promise.all([
                api.get('/api/v1/critical-nodes'),
                api.get('/api/v1/critical-nodes/stats')
            ]);
            setNodes(nodesRes.data.content || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading critical nodes:', error);
        } finally {
            setLoading(false);
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'HIGH': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'MEDIUM': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('nodes.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('nodes.subtitle')}</p>
                </div>
                <button
                    onClick={loadData}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg"
                >
                    {t('nodes.checkConflicts')}
                </button>
            </div>

            {stats && (
                <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                        <div className="text-gray-400 text-sm">{t('nodes.stats.total')}</div>
                        <div className="text-2xl font-bold text-white mt-1">{stats.total}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 border border-red-500/30">
                        <div className="text-red-400 text-sm">{t('nodes.stats.detected')}</div>
                        <div className="text-2xl font-bold text-red-400 mt-1">{stats.detected}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 border border-yellow-500/30">
                        <div className="text-yellow-400 text-sm">{t('nodes.stats.underReview')}</div>
                        <div className="text-2xl font-bold text-yellow-400 mt-1">{stats.underReview}</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-4 border border-green-500/30">
                        <div className="text-green-400 text-sm">{t('nodes.stats.resolved')}</div>
                        <div className="text-2xl font-bold text-green-400 mt-1">{stats.resolved}</div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            ) : nodes.length === 0 ? (
                <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-12 text-center">
                    <div className="text-green-400 text-xl font-semibold mb-2">{t('nodes.empty.title')}</div>
                    <p className="text-gray-400">{t('nodes.empty.description')}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {nodes.map((node) => (
                        <div
                            key={node.id}
                            className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-5 hover:border-gray-600 transition-colors"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <span className="font-mono text-sm text-white">{node.code}</span>
                                    <div className="text-xs text-gray-500 mt-0.5">{node.type}</div>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(node.severity)}`}>
                                    {node.severity}
                                </span>
                            </div>
                            <p className="text-gray-300 text-sm mb-4">{node.description}</p>
                            <div className="text-xs text-gray-500">
                                {new Date(node.detectedAt).toLocaleDateString('es-CL')}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
